// Review pages

import { NavigationMenuLinks } from "@/components/NavigationMenu";

const reviewCategoryIdToKeyMap = {
  "1": "usage",
  "2": "device",
  "3": "network",
  "4": "datacenter",
  "5": "operations",
  "6": "internal",
  "7": "external",
  "8": "event",
  "9": "downstream_delivery",
  "10": "support",
  "11": "production",
  "12": "furniture",
  "13": "upstream_delivery",
  "14": "service",
  "15": "office",
  "16": "building",
  "17": "energy",
  "18": "waste",
  "19": "cleaning",
  "20": "food",
  "21": "transport",
  "22": "commute",
  "23": "trips",
  "25": "other",
  "24": "investment",
  "34": "other_gas",
  "35": "without_category",
  "67": "industrial_equipment",
  "68": "product_use",
  "69": "product_eol",
};

type TReviewCategoryId = keyof typeof reviewCategoryIdToKeyMap;

export const publicReviewSubCategoryHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.restitution,
  breadcrumb: ({
    reviewId,
    reviewCategoryId,
  }: {
    reviewId: string;
    reviewCategoryId: TReviewCategoryId;
  }) => [
    {
      href: `/reviews/${reviewId}/public/${reviewCategoryId}`,
      label: `categories:${reviewCategoryIdToKeyMap[reviewCategoryId]}`,
    },
  ],
};

export const publicReviewIndexHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.restitution,
  breadcrumb: ({ slug }: { slug: string }) => [
    {
      href: `/reviews/${slug}/public`,
      label: `navigation-menu:pages.analyse`,
    },
  ],
};

export const reviewSubCategoryHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.restitution,
  breadcrumb: ({
    reviewId,
    reviewCategoryId,
  }: {
    reviewId: string;
    reviewCategoryId: TReviewCategoryId;
  }) => [
    {
      href: `/reviews/${reviewId}/review_categories/${reviewCategoryId}`,
      label: `categories:${reviewCategoryIdToKeyMap[reviewCategoryId]}`,
    },
  ],
};

// Collect pages

const categoriesIdMap = {
  "1": "usage",
  "2": "operations",
  "3": "support",
  "4": "office",
  "5": "transport",
  "6": "other",
};

type TCollectCategoryId = keyof typeof categoriesIdMap;

const categoriesNameMap = Object.fromEntries(
  Object.entries(categoriesIdMap).map(([id, name]) => [name, id])
);

const getCategoryFromSubCategory = (subCategory: string) => {
  switch (subCategory) {
    case "websites":
    case "applications":
    case "softwares":
    case "tech_tools":
    case "apis":
      return categoriesIdMap["1"];
    case "communications":
    case "events":
    case "tools":
    case "phones":
    case "downstream_deliveries":
      return categoriesIdMap["2"];
    case "office_furnitures":
    case "furniture_items":
    case "services":
    case "upstream_deliveries":
    case "products":
    case "industrial_equipments":
      return categoriesIdMap["3"];
    case "offices":
    case "wastes":
    case "cleanings":
    case "foods":
      return categoriesIdMap["4"];
    case "commutes":
    case "flights":
    case "trains":
    case "vehicles":
    case "other_transports":
      return categoriesIdMap["5"];
    case "others":
    case "investments":
    case "other_gases":
      return categoriesIdMap["6"];

    default:
      return "1";
  }
};

export const trialHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: () => {
    return [
      {
        href: `/trial`,
        label: `categories:measure`,
      },
    ];
  },
};

export const collectHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: ({ scoreId: paramsScoreId }: { scoreId: string }) => {
    const scoreId = paramsScoreId ?? localStorage.getItem("currentScoreId");
    return [
      {
        href: `/scores/${scoreId}/categories`,
        label: `categories:measure`,
      },
    ];
  },
};

export const collectGeneralInfoHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: () => {
    return [
      {
        href: `/tracking/campaigns/id/edit`,
        label: `categories:generalInfo`,
      },
    ];
  },
};

export const collectCategoryHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: ({
    scoreId,
    categoryId,
  }: {
    scoreId: string;
    categoryId: TCollectCategoryId;
  }) => {
    const category = categoriesIdMap[categoryId];

    return [
      {
        href: `/scores/${scoreId}/categories/${categoriesNameMap[category]}`,
        label: `categories:${category}`,
      },
    ];
  },
};

export const collectSurveyHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: ({
    scoreId,
    subCategory,
  }: {
    scoreId: string;
    subCategory: string;
  }) => {
    const category = getCategoryFromSubCategory(subCategory);

    return [
      {
        href: `/scores/${scoreId}/categories/${categoriesNameMap[category]}`,
        label: `categories:${category}`,
      },
    ];
  },
};

export const collectSurveyCreateHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: ({ subCategory }: { subCategory: string }) => {
    const scoreId = localStorage.getItem("currentScoreId");
    const category = getCategoryFromSubCategory(subCategory);

    return [
      {
        href: `/scores/${scoreId}/categories`,
        label: `categories:measure`,
      },
      {
        href: `/scores/${scoreId}/categories/${categoriesNameMap[category]}`,
        label: `categories:${category}`,
      },
      {
        href: `/scores/${scoreId}/survey/${subCategory}`,
        label: `categories:${subCategory}`,
      },
      {
        href: `/scores/${scoreId}/survey/${subCategory}/new`,
        label: `categories:new`,
      },
    ];
  },
};

export const collectSubCategoryHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: ({
    scoreId,
    subCategory,
  }: {
    scoreId: string;
    subCategory: string;
  }) => {
    return [
      {
        href: `/scores/${scoreId}/survey/${subCategory}`,
        label: `categories:${subCategory}`,
      },
    ];
  },
};

export const collectEmployeeSurveyHandle = {
  isOverlayed: true,
  breadcrumb: ({ scoreId }: { scoreId: string }) => {
    return [
      {
        href: `/scores/${scoreId}/survey/team/report`,
        label: `categories:team`,
        activeMenuLink: NavigationMenuLinks.collect,
      },
    ];
  },
};

export const collectImportedDepositsHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: ({ scoreId }: { scoreId: string }) => {
    return [
      {
        href: `/scores/${scoreId}/import_deposits`,
        label: `categories:ImportedFiles`,
      },
    ];
  },
};

export const collectImportSummaryHandle = {
  isOverlayed: true,
  activeMenuLink: NavigationMenuLinks.collect,
  breadcrumb: ({
    scoreId,
    importId,
  }: {
    scoreId: string;
    importId: string;
  }) => {
    return [
      {
        href: `/scores/${scoreId}/import_deposits`,
        label: `categories:ImportedFiles`,
      },
      {
        href: `/scores/${scoreId}/import_deposits/${importId}`,
        label: `categories:importSummary`,
      },
    ];
  },
};
